import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * ONE RULE FOR EVERY FIELD IN THE APP, and it lives here so that a component
 * written next year inherits it without anybody remembering to ask.
 *
 *   background  `bg-input-background`  — #ffffff light, #0f0b1f dark. Recessed
 *                                        below --card, so a field reads as a
 *                                        well you type into rather than as a
 *                                        panel.
 *   border      `border-input`         — the field border token. Not `border`,
 *                                        which falls through to --border and
 *                                        is a different, lighter colour.
 *   placeholder `placeholder:text-muted-foreground`
 *   focus ring  `focus-visible:border-ring focus-visible:ring-ring/50
 *                focus-visible:ring-[3px]`
 *   invalid     `aria-invalid:border-destructive aria-invalid:ring-destructive/20`
 *
 * THERE WAS NO `dark:bg-input/30` HERE AND THERE MUST NOT BE ONE AGAIN.
 * Input, Select, ScrollSafeSelect and Checkbox all used to carry both
 * `bg-input-background` and `dark:bg-input/30`. Both set background-color, and
 * the dark variant compiles to `&:is(.dark *)` — specificity (0,2,0) against
 * the base utility's (0,1,0) — so it won every time and `--input-background`'s
 * dark value was never drawn by any primitive. The token looked wired and was
 * not. Worse, `--input` is documented as the field BORDER colour, so a field's
 * surface was being painted from the border token at 30% alpha, which meant it
 * picked up whatever sat behind it: the same field read as one colour on a
 * card and another on the page.
 *
 * Textarea had the opposite half of the bug — `bg-transparent` and no
 * `bg-input-background` at all — so in light mode a textarea was see-through
 * while the input above it was white.
 *
 * fieldStyles.ts in the account-opening wizard already encodes this rule and
 * records the same lesson in its own header. These primitives now agree with
 * it, and `formTokens.test.ts` holds all of them to it.
 */
function Input({ className, type, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input h-9 w-full min-w-0 rounded-md border bg-input-background px-3 py-1 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        className,
      )}
      {...props}
    />
  );
}

export { Input };
